import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, FileText, Loader2, MapPin, Plus, Save, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/api/client';
import AddressFields from '@/components/settings/AddressFields';
import ApiKeysSection from '@/components/settings/ApiKeysSection';
import ShippingProviderSection from '@/components/settings/ShippingProviderSection';
import EasyPostWebhookSection from '@/components/settings/EasyPostWebhookSection';
import MailSection from '@/components/settings/MailSection';
import PrintingSection from '@/components/settings/PrintingSection';
import BoxCard from '@/components/settings/BoxCard';
import BoxForm from '@/components/settings/BoxForm';
import DeliveryAddressCard from '@/components/settings/DeliveryAddressCard';
import DeliveryAddressForm from '@/components/settings/DeliveryAddressForm';
import MediaCard from '@/components/settings/MediaCard';
import MediaForm from '@/components/settings/MediaForm';
import ReturnAddressCard from '@/components/settings/ReturnAddressCard';
import ReturnAddressForm from '@/components/settings/ReturnAddressForm';
import UsersSection from '@/components/settings/UsersSection';
import { FieldError } from '@/components/settings/form-utils';
import { errorClass, useFieldErrors } from '@/components/settings/use-field-errors';
import { BUILD_STAMP } from '@/lib/build-info';

export default function SettingsPage({ user }) {
  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Settings</h1>
        <p className="text-slate-600">Configure shipping defaults and who can use the app</p>
      </div>

      {user.role === 'admin' && <ApiKeysSection />}
      {user.role === 'admin' && <ShippingProviderSection />}
      {user.role === 'admin' && <EasyPostWebhookSection />}
      {user.role === 'admin' && <MailSection currentUserEmail={user.email} />}
      {user.role === 'admin' && <PrintingSection />}
      <SenderAddressSection />
      <CollectionSection
        title="Shipping boxes"
        icon={Box}
        gradient="from-purple-50 to-indigo-50"
        buttonClass="bg-purple-600 hover:bg-purple-700"
        queryKey="boxes"
        queryFn={() => api.entities.ShippingBox.list()}
        emptyText="No shipping boxes yet"
        emptyHint='Add the box sizes you ship in — they appear when starting a bulk run.'
        renderCard={(box, onEdit) => <BoxCard key={box.id} box={box} onEdit={onEdit} />}
        renderForm={(editing, onClose) => <BoxForm box={editing} onClose={onClose} />}
        addLabel="Add box"
      />
      <CollectionSection
        title="Media types"
        icon={FileText}
        gradient="from-amber-50 to-orange-50"
        buttonClass="bg-amber-600 hover:bg-amber-700"
        queryKey="media"
        queryFn={() => api.entities.Media.list()}
        emptyText="No media types yet"
        emptyHint="Add the label stock and envelope sizes you print on."
        renderCard={(media, onEdit) => <MediaCard key={media.id} media={media} onEdit={onEdit} />}
        renderForm={(editing, onClose) => <MediaForm media={editing} onClose={onClose} />}
        addLabel="Add media"
      />
      <CollectionSection
        title="Return addresses (ship from)"
        icon={MapPin}
        gradient="from-red-50 to-pink-50"
        buttonClass="bg-red-600 hover:bg-red-700"
        queryKey="returnAddresses"
        queryFn={() => api.entities.ReturnAddress.list()}
        emptyText="No return addresses yet"
        emptyHint="Add at least one — single labels need somewhere to ship from."
        renderCard={(address, onEdit) => (
          <ReturnAddressCard key={address.id} address={address} onEdit={onEdit} />
        )}
        renderForm={(editing, onClose) => (
          <ReturnAddressForm address={editing} onClose={onClose} />
        )}
        addLabel="Add address"
      />
      <CollectionSection
        title="Delivery addresses (ship to)"
        icon={MapPin}
        gradient="from-green-50 to-emerald-50"
        buttonClass="bg-green-600 hover:bg-green-700"
        queryKey="deliveryAddresses"
        queryFn={() => api.entities.DeliveryAddress.list()}
        emptyText="No saved delivery addresses"
        emptyHint="Addresses saved from the Mail page show up here for reuse."
        renderCard={(address, onEdit) => (
          <DeliveryAddressCard key={address.id} address={address} onEdit={onEdit} />
        )}
        renderForm={(editing, onClose) => (
          <DeliveryAddressForm address={editing} onClose={onClose} />
        )}
        addLabel="Add address"
      />

      {user.role === 'admin' && <UsersSection currentUser={user} />}

      <p className="text-xs text-center text-slate-400 pb-4">ShipSwift {BUILD_STAMP}</p>
    </div>
  );
}

/**
 * The sender identity Shippo bills and ships bulk runs from.
 *
 * The Base44 version kept the Shippo API key on this record, which meant any
 * signed-in user's browser could read it back. Keys now live in their own
 * admin-only section and are write-only — see ApiKeysSection.
 */
function SenderAddressSection() {
  const queryClient = useQueryClient();
  const { fieldErrors, handleError, clearErrors } = useFieldErrors();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['appSettings'],
    queryFn: () => api.entities.AppSettings.get(),
  });

  const [formData, setFormData] = useState({
    senderName: '',
    senderPhone: '',
    senderEmail: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  });

  useEffect(() => {
    if (!settings) return;
    setFormData({
      senderName: settings.senderName ?? '',
      senderPhone: settings.senderPhone ?? '',
      senderEmail: settings.senderEmail ?? '',
      address1: settings.senderAddress1 ?? '',
      address2: settings.senderAddress2 ?? '',
      city: settings.senderCity ?? '',
      state: settings.senderState ?? '',
      zip: settings.senderZip ?? '',
      country: settings.senderCountry ?? 'US',
    });
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.entities.AppSettings.save({
        senderName: formData.senderName,
        senderPhone: formData.senderPhone,
        senderEmail: formData.senderEmail,
        senderAddress1: formData.address1,
        senderAddress2: formData.address2,
        senderCity: formData.city,
        senderState: formData.state,
        senderZip: formData.zip,
        senderCountry: formData.country,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appSettings'] });
      toast.success('Sender address saved');
    },
    // The API returns errors keyed by its own field names (senderCity), while
    // this form uses short ones (city). Remap so the message lands on the input
    // the person is looking at rather than vanishing.
    onError: (error) => {
      if (error?.details) {
        error.details = Object.fromEntries(
          Object.entries(error.details).map(([key, message]) => [
            key.replace(/^sender(Address1|Address2|City|State|Zip|Country)$/, (_m, part) =>
              part.charAt(0).toLowerCase() + part.slice(1),
            ),
            message,
          ]),
        );
      }
      handleError(error, 'Failed to save sender address');
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    clearErrors();
    saveMutation.mutate();
  };

  if (isLoading) {
    return (
      <Card className="border-none shadow-lg">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardTitle className="flex items-center gap-2">
          <Truck className="w-5 h-5" />
          Sender address
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sender-name">Sender name</Label>
            <Input
              id="sender-name"
              value={formData.senderName}
              onChange={(e) => setFormData({ ...formData, senderName: e.target.value })}
              placeholder="Gold Star Printing"
              className={errorClass(fieldErrors.senderName)}
              required
            />
            <FieldError message={fieldErrors.senderName} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sender-phone">Phone number</Label>
            <Input
              id="sender-phone"
              type="tel"
              value={formData.senderPhone}
              onChange={(e) => setFormData({ ...formData, senderPhone: e.target.value })}
              placeholder="555-555-0100"
              className={errorClass(fieldErrors.senderPhone)}
              required
            />
            <FieldError message={fieldErrors.senderPhone} />
            <p className="text-xs text-slate-500">
              Required — USPS will not return rates without a sender phone number.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sender-email">Sender email</Label>
            <Input
              id="sender-email"
              type="email"
              value={formData.senderEmail}
              onChange={(e) => setFormData({ ...formData, senderEmail: e.target.value })}
              placeholder="shipping@goldstarprinting.us"
              className={errorClass(fieldErrors.senderEmail)}
              required
            />
            <FieldError message={fieldErrors.senderEmail} />
            <p className="text-xs text-slate-500">
              Printed on every label as the contact address, and where carriers send delivery
              notices. Use a shop address rather than a personal one, so it keeps working when
              someone leaves.
            </p>
          </div>

          <AddressFields
            formData={formData}
            setFormData={setFormData}
            fieldErrors={fieldErrors}
            idPrefix="sender"
          />

          <Button
            type="submit"
            disabled={saveMutation.isPending}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save sender address
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/** List + add/edit form for one collection. The four sections differ only in data. */
function CollectionSection({
  title,
  icon: Icon,
  gradient,
  buttonClass,
  queryKey,
  queryFn,
  emptyText,
  emptyHint,
  renderCard,
  renderForm,
  addLabel,
}) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: items = [], isLoading } = useQuery({ queryKey: [queryKey], queryFn });

  const close = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setShowForm(true);
  };

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className={`border-b bg-gradient-to-r ${gradient}`}>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5" />
            {title}
          </CardTitle>
          <Button onClick={() => setShowForm(true)} size="sm" className={buttonClass}>
            <Plus className="w-4 h-4 mr-2" />
            {addLabel}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        {showForm && (
          // Keyed so switching between editing two records remounts the form
          // rather than leaving the previous record's values in the inputs.
          <div className="mb-6">
            <div key={editing?.id ?? 'new'}>{renderForm(editing, close)}</div>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Icon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">{emptyText}</p>
            <p className="text-sm mt-1">{emptyHint}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((item) => renderCard(item, handleEdit))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
